import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db } from './services/firebase';
import Navbar from './components/Navbar';
import Home from './components/Home';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import MemberForm from './components/Forms/MemberForm';
import PartnerForm from './components/Forms/PartnerForm';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import MemberCoordinator from './components/Dashboard/MemberCoordinator';
import PartnerCoordinator from './components/Dashboard/PartnerCoordinator';
import ClubCoordinator from './components/Dashboard/ClubCoordinator/ClubCoordinator';
import RoleManager from './components/Admin/RoleManager';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentView, setCurrentView] = useState('home');
  const [loading, setLoading] = useState(true);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [showAdminLoginModal, setShowAdminLoginModal] = useState(false);
  const [adminLoginData, setAdminLoginData] = useState({
    email: '',
    password: ''
  });
  const [adminLoginError, setAdminLoginError] = useState('');
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);

  useEffect(() => {
    let unsubscribeUser = () => {};
    
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setLoading(true);
        setRoleLoaded(false);
        
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          
          // Set up a real-time listener for user role changes
          unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
              const userData = docSnapshot.data();
              const newRole = userData.role || 'user';
              setUserRole(newRole);
              setRoleLoaded(true);
              
              // Auto-redirect based on role
              if (newRole === 'admin' && currentView === 'home') {
                setCurrentView('admin');
              } else if (newRole === 'member_coordinator' && currentView === 'home') {
                setCurrentView('member_coordinator');
              } else if (newRole === 'partner_coordinator' && currentView === 'home') {
                setCurrentView('partner_coordinator');
              } else if (newRole === 'club_coordinator' && currentView === 'home') {
                setCurrentView('club_coordinator');
              }
            } else {
              // If user document doesn't exist
              setUserRole('user');
              setRoleLoaded(true);
            }
            setLoading(false);
          }, (error) => {
            console.error('Error listening to user role:', error);
            setUserRole('user');
            setRoleLoaded(true);
            setLoading(false);
          });
        } catch (error) {
          console.error('Error setting up user listener:', error);
          setUserRole('user');
          setRoleLoaded(true);
          setLoading(false);
        }
      } else {
        setUser(null);
        setUserRole(null);
        setRoleLoaded(false);
        setLoading(false);
        unsubscribeUser();
      }
    });
    
    // Cleanup function
    return () => {
      unsubscribeAuth();
      unsubscribeUser();
    };
  }, [currentView]);

  const handleLogout = () => {
    signOut(auth).then(() => {
      setUser(null);
      setUserRole(null);
      setRoleLoaded(false);
      setCurrentView('home');
    });
  };

  const handleAdminLogin = async () => {
    try {
      setAdminLoginError('');
      setAdminLoginLoading(true);
      
      if (!adminLoginData.email || !adminLoginData.password) {
        setAdminLoginError('Please enter both email and password');
        setAdminLoginLoading(false);
        return;
      }
      
      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(
        auth, 
        adminLoginData.email, 
        adminLoginData.password
      );
      
      // Check if the user has admin role in Firestore
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // User document doesn't exist - not an admin
        await signOut(auth); // Sign out immediately
        setAdminLoginError('This account does not have admin privileges');
        setAdminLoginLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      if (userData.role !== 'admin') {
        // User is not an admin
        await signOut(auth); // Sign out immediately
        setAdminLoginError('This account does not have admin privileges');
        setAdminLoginLoading(false);
        return;
      }
      
      // User is a valid admin
      setShowAdminLoginModal(false);
      setAdminLoginData({ email: '', password: '' });
      setAdminLoginLoading(false);
      
    } catch (error) {
      console.error('Admin login error:', error);
      if (error.code === 'auth/invalid-credential') {
        setAdminLoginError('Invalid email or password');
      } else if (error.code === 'auth/user-not-found') {
        setAdminLoginError('No account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        setAdminLoginError('Incorrect password');
      } else {
        setAdminLoginError('Login failed: ' + error.message);
      }
      setAdminLoginLoading(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    // Show loading until role is properly loaded
    if (user && !roleLoaded) {
      return <div className="loading">Loading your permissions...</div>;
    }

    if (!user) {
      switch(currentView) {
        case 'login': return <Login setCurrentView={setCurrentView} />;
        case 'register': return <Register setCurrentView={setCurrentView} />;
        default: return <Home setCurrentView={setCurrentView} />;
      }
    }
    
    // Check user role and render appropriate dashboard
    switch(userRole) {
      case 'admin':
        if (currentView === 'role_manager') {
          return <RoleManager />;
        } else if (currentView === 'admin') {
          return <AdminDashboard />;
        }
        return <AdminDashboard />;
        
      case 'member_coordinator':
        return <MemberCoordinator />;
        
      case 'partner_coordinator':
        return <PartnerCoordinator />;
        
      case 'club_coordinator':
        return <ClubCoordinator />;
        
      default:
        // Regular user views
        switch(currentView) {
          case 'member': return <MemberForm user={user} />;
          case 'partner': return <PartnerForm user={user} />;
          default: return <Home user={user} setCurrentView={setCurrentView} />;
        }
    }
  };

  return (
    <div className="App">
      <Navbar 
        user={user} 
        userRole={userRole} 
        currentView={currentView} 
        setCurrentView={setCurrentView} 
        handleLogout={handleLogout}
        onLogoClick={() => setShowAdminLoginModal(true)}
      />
      
      <main className="main-content">
        {renderContent()}
      </main>

      {/* Admin Login Modal */}
      {showAdminLoginModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Admin Login</h3>
              <button className="modal-close" onClick={() => {
                setShowAdminLoginModal(false);
                setAdminLoginError('');
                setAdminLoginData({ email: '', password: '' });
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Enter admin email"
                  value={adminLoginData.email}
                  onChange={(e) => setAdminLoginData({...adminLoginData, email: e.target.value})}
                  disabled={adminLoginLoading}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  placeholder="Enter admin password"
                  value={adminLoginData.password}
                  onChange={(e) => setAdminLoginData({...adminLoginData, password: e.target.value})}
                  disabled={adminLoginLoading}
                />
              </div>
              {adminLoginError && (
                <div className="error-text">{adminLoginError}</div>
              )}
              <div className="admin-setup-info">
                <h4>Admin Account Setup Required:</h4>
                <ol>
                  <li>Create a regular user account first through registration</li>
                  <li>Manually set the user role to "admin" in Firebase Firestore</li>
                  <li>Use those credentials to login here</li>
                </ol>
                <p><strong>Note:</strong> Regular user accounts cannot access admin features even with correct password.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => {
                setShowAdminLoginModal(false);
                setAdminLoginError('');
                setAdminLoginData({ email: '', password: '' });
              }} disabled={adminLoginLoading}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleAdminLogin} disabled={adminLoginLoading}>
                {adminLoginLoading ? 'Logging in...' : 'Login as Admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;